import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';

import { clearCallDebugLog, subscribeCallDebugLog, type CallDebugLogEntry } from 'vcs-common';

const LEVEL_COLOR: Record<CallDebugLogEntry['level'], string> = {
  info: 'text-[#9CDCFE]',
  warn: 'text-[#FFD866]',
  error: 'text-[#FF6B6B]',
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${d.toLocaleTimeString('ru-RU', { hour12: false })}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}

/**
 * Диагностическая панель звонка: показывает поверх экрана события подписки на
 * треки (кто на кого подписался, когда создалась плитка участника, догнал ли
 * поздний трек уже смонтированный видеоэлемент). Нужна для тестирования на
 * iPad/iPhone/MacBook, где нет удобного доступа к devtools во время звонка.
 *
 * Открывается/скрывается плавающей кнопкой — сам по себе не занимает место,
 * пока не открыт.
 */
export function CallDebugPanel() {
  if (import.meta.env.PROD) {
    return null;
  }

  return <CallDebugPanelContent />;
}

function CallDebugPanelContent() {
  const [isOpen, setIsOpen] = useState(false);
  const [entries, setEntries] = useState<CallDebugLogEntry[]>([]);
  const [scopeFilter, setScopeFilter] = useState<string>('all');

  useEffect(() => {
    return subscribeCallDebugLog(setEntries);
  }, []);

  const scopes = useMemo(() => {
    const set = new Set<string>();
    entries.forEach((entry) => set.add(entry.scope));
    return Array.from(set);
  }, [entries]);

  const filteredEntries = useMemo(
    () => (scopeFilter === 'all' ? entries : entries.filter((entry) => entry.scope === scopeFilter)),
    [entries, scopeFilter],
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={classNames(
          'fixed z-[999999999] bottom-[100px] right-[10px] w-10 h-10 rounded-full flex items-center justify-center text-[16px] shadow-lg',
          isOpen ? 'bg-[#C00000] text-white' : 'bg-black/60 text-white',
        )}
        aria-label={isOpen ? 'Скрыть диагностику звонка' : 'Показать диагностику звонка'}
        title="Диагностика звонка"
      >
        🐞
      </button>

      {isOpen && (
        <div className="fixed inset-x-0 bottom-0 z-[999999998] h-[45vh] bg-black/90 text-white flex flex-col font-mono text-[11px]">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/20 shrink-0">
            <span className="font-bold">Диагностика звонка ({filteredEntries.length})</span>
            <select
              value={scopeFilter}
              onChange={(event) => setScopeFilter(event.target.value)}
              className="bg-white/10 rounded px-2 py-1 text-white"
            >
              <option value="all">все ({entries.length})</option>
              {scopes.map((scope) => (
                <option key={scope} value={scope}>
                  {scope}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => clearCallDebugLog()} className="ml-auto bg-white/10 rounded px-3 py-1">
              Очистить
            </button>
            <button type="button" onClick={() => setIsOpen(false)} className="bg-white/10 rounded px-3 py-1">
              Закрыть
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2 flex flex-col gap-1">
            {filteredEntries.length === 0 && <div className="text-white/50">Пока пусто</div>}
            {filteredEntries.map((entry) => (
              <div key={entry.id} className="leading-tight">
                <span className="text-white/40">{formatTime(entry.time)}</span>{' '}
                <span className="text-white/60">[{entry.scope}]</span>{' '}
                <span className={LEVEL_COLOR[entry.level]}>{entry.message}</span>
                {entry.data && (
                  <div className="text-white/50 pl-4 whitespace-pre-wrap break-all">{JSON.stringify(entry.data)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
